import React, { useEffect, useState, useCallback, useRef } from 'react';
import { isMobile, getDeviceType, getDevicePixelRatio, getViewportDimensions } from '../utils/device';

// Enhanced interfaces for comprehensive performance monitoring
interface EnhancedPerformanceConfig {
  enableAdaptiveLoading: boolean;
  enableBatteryOptimization: boolean;
  enableConnectionAwareness: boolean;
  performanceThresholds: {
    slowConnection: number;
    lowBattery: number;
    lowMemory: number;
  };
}

interface PerformanceMetrics {
  // Device Information
  deviceType: 'mobile' | 'tablet' | 'desktop';
  screenSize: { width: number; height: number };
  pixelRatio: number;
  hardwareConcurrency: number;
  deviceMemory: number;
  
  // Network Information
  connectionType: string;
  effectiveType: '2g' | '3g' | '4g' | 'slow-2g' | 'unknown';
  downlink: number;
  rtt: number;
  saveData: boolean;
  
  // Battery Information
  batteryLevel: number;
  batteryCharging: boolean;
  
  // Performance Metrics
  loadTime: number;
  firstContentfulPaint: number;
  largestContentfulPaint: number;
  cumulativeLayoutShift: number;
  firstInputDelay: number;
  
  // Computed flags
  isLowEndDevice: boolean;
  isSlowConnection: boolean;
  isLowBattery: boolean;
}

interface AdaptiveLoadingStrategy {
  enableImageOptimization: boolean;
  enableLazyLoading: boolean;
  enableAnimationReduction: boolean;
  enableDataSaving: boolean;
  imageQuality: 'low' | 'medium' | 'high';
  chunkLoadingStrategy: 'aggressive' | 'conservative' | 'minimal';
}

interface EnhancedMobilePerformanceMonitorProps {
  children: React.ReactNode;
  config?: Partial<EnhancedPerformanceConfig>;
  onMetricsUpdate?: (metrics: PerformanceMetrics) => void;
  onStrategyChange?: (strategy: AdaptiveLoadingStrategy) => void;
}

const defaultConfig: EnhancedPerformanceConfig = {
  enableAdaptiveLoading: true,
  enableBatteryOptimization: true,
  enableConnectionAwareness: true,
  performanceThresholds: {
    slowConnection: 1.5, // Mbps
    lowBattery: 0.2, // 20%
    lowMemory: 4, // GB
  },
};

/**
 * Enhanced Mobile Performance Monitor Component
 * Provides comprehensive device detection, performance monitoring, and adaptive loading strategies
 */
const EnhancedMobilePerformanceMonitor: React.FC<EnhancedMobilePerformanceMonitorProps> = ({
  children,
  config = {},
  onMetricsUpdate,
  onStrategyChange,
}) => {
  const [metrics, setMetrics] = useState<PerformanceMetrics | null>(null);
  const [strategy, setStrategy] = useState<AdaptiveLoadingStrategy | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  
  const metricsRef = useRef<PerformanceMetrics | null>(null);
  const configRef = useRef<EnhancedPerformanceConfig>({ ...defaultConfig, ...config });

  // Device detection utilities
  const detectDeviceCapabilities = useCallback((): Partial<PerformanceMetrics> => {
    const viewport = getViewportDimensions();
    
    return {
      deviceType: getDeviceType(),
      screenSize: viewport,
      pixelRatio: getDevicePixelRatio(),
      hardwareConcurrency: navigator.hardwareConcurrency || 2,
      deviceMemory: (navigator as any).deviceMemory || 4,
    };
  }, []);

  // Network information detection
  const detectNetworkInfo = useCallback((): Partial<PerformanceMetrics> => {
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    
    if (!connection) {
      return {
        connectionType: 'unknown',
        effectiveType: 'unknown',
        downlink: 10, // Assume good connection
        rtt: 100,
        saveData: false,
      };
    }

    return {
      connectionType: connection.type || 'unknown',
      effectiveType: connection.effectiveType || '4g',
      downlink: connection.downlink || 10,
      rtt: connection.rtt || 100,
      saveData: connection.saveData || false,
    };
  }, []);

  // Battery information detection
  const detectBatteryInfo = useCallback(async (): Promise<Partial<PerformanceMetrics>> => {
    if (!('getBattery' in navigator)) {
      return {
        batteryLevel: 1,
        batteryCharging: true,
      };
    }

    try {
      const battery = await (navigator as any).getBattery();
      return {
        batteryLevel: battery.level,
        batteryCharging: battery.charging,
      };
    } catch (error) {
      return {
        batteryLevel: 1,
        batteryCharging: true,
      };
    }
  }, []);

  // Performance metrics collection
  const collectPerformanceMetrics = useCallback((): Partial<PerformanceMetrics> => {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    const paint = performance.getEntriesByType('paint');
    
    const fcp = paint.find(entry => entry.name === 'first-contentful-paint')?.startTime || 0;
    const lcp = performance.getEntriesByType('largest-contentful-paint')[0]?.startTime || 0;
    
    return {
      loadTime: navigation ? navigation.loadEventEnd - navigation.startTime : 0,
      firstContentfulPaint: fcp,
      largestContentfulPaint: lcp,
      cumulativeLayoutShift: 0, // Would need to implement CLS observer
      firstInputDelay: 0, // Would need to implement FID observer
    };
  }, []);

  // Compute device and connection flags
  const computeFlags = useCallback((partialMetrics: Partial<PerformanceMetrics>): Partial<PerformanceMetrics> => {
    const { performanceThresholds } = configRef.current;
    
    const isLowEndDevice = 
      (partialMetrics.hardwareConcurrency || 0) < 4 ||
      (partialMetrics.deviceMemory || 0) < performanceThresholds.lowMemory ||
      (partialMetrics.screenSize?.width || 0) < 375; // Small screen indicator

    const isSlowConnection = 
      (partialMetrics.downlink || 0) < performanceThresholds.slowConnection ||
      partialMetrics.effectiveType === '2g' ||
      partialMetrics.effectiveType === 'slow-2g';

    const isLowBattery = 
      (partialMetrics.batteryLevel || 1) < performanceThresholds.lowBattery &&
      !partialMetrics.batteryCharging;

    return {
      ...partialMetrics,
      isLowEndDevice,
      isSlowConnection,
      isLowBattery,
    };
  }, []);

  // Generate adaptive loading strategy based on metrics
  const generateAdaptiveStrategy = useCallback((metrics: PerformanceMetrics): AdaptiveLoadingStrategy => {
    const { enableAdaptiveLoading, enableBatteryOptimization, enableConnectionAwareness } = configRef.current;

    if (!enableAdaptiveLoading) {
      return {
        enableImageOptimization: false,
        enableLazyLoading: true,
        enableAnimationReduction: false,
        enableDataSaving: false,
        imageQuality: 'high',
        chunkLoadingStrategy: 'aggressive',
      };
    }

    // Determine image quality based on connection and device
    let imageQuality: 'low' | 'medium' | 'high' = 'high';
    if (metrics.isSlowConnection || metrics.saveData) {
      imageQuality = 'low';
    } else if (metrics.isLowEndDevice) {
      imageQuality = 'medium';
    }

    // Determine chunk loading strategy
    let chunkLoadingStrategy: 'aggressive' | 'conservative' | 'minimal' = 'aggressive';
    if (metrics.isSlowConnection || metrics.isLowBattery) {
      chunkLoadingStrategy = 'minimal';
    } else if (metrics.isLowEndDevice) {
      chunkLoadingStrategy = 'conservative';
    }

    return {
      enableImageOptimization: enableConnectionAwareness && (metrics.isSlowConnection || metrics.saveData),
      enableLazyLoading: true,
      enableAnimationReduction: enableBatteryOptimization && (metrics.isLowBattery || metrics.isLowEndDevice),
      enableDataSaving: enableConnectionAwareness && (metrics.saveData || metrics.isSlowConnection),
      imageQuality,
      chunkLoadingStrategy,
    };
  }, []);

  // Apply performance optimizations to DOM
  const applyPerformanceOptimizations = useCallback((strategy: AdaptiveLoadingStrategy) => {
    const body = document.body;
    
    // Remove existing performance classes
    body.classList.remove(
      'performance-mode',
      'reduce-animations',
      'low-memory-mode',
      'data-saver-mode',
      'image-optimization-mode'
    );

    // Apply new performance classes based on strategy
    if (strategy.enableAnimationReduction) {
      body.classList.add('reduce-animations');
    }

    if (strategy.enableDataSaving) {
      body.classList.add('data-saver-mode');
    }

    if (strategy.enableImageOptimization) {
      body.classList.add('image-optimization-mode');
    }

    // Set CSS custom properties for dynamic optimization
    body.style.setProperty('--image-quality', strategy.imageQuality);
    body.style.setProperty('--chunk-loading-strategy', strategy.chunkLoadingStrategy);
  }, []);

  // Main metrics collection function
  const collectAllMetrics = useCallback(async (): Promise<PerformanceMetrics> => {
    const deviceInfo = detectDeviceCapabilities();
    const networkInfo = detectNetworkInfo();
    const batteryInfo = await detectBatteryInfo();
    const performanceInfo = collectPerformanceMetrics();

    const partialMetrics = {
      ...deviceInfo,
      ...networkInfo,
      ...batteryInfo,
      ...performanceInfo,
    };

    const completeMetrics = computeFlags(partialMetrics) as PerformanceMetrics;
    return completeMetrics;
  }, [detectDeviceCapabilities, detectNetworkInfo, detectBatteryInfo, collectPerformanceMetrics, computeFlags]);

  // Initialize performance monitoring
  useEffect(() => {
    if (!isMobile() && getDeviceType() !== 'tablet') {
      setIsInitialized(true);
      return;
    }

    const initializeMonitoring = async () => {
      try {
        const initialMetrics = await collectAllMetrics();
        const initialStrategy = generateAdaptiveStrategy(initialMetrics);

        setMetrics(initialMetrics);
        setStrategy(initialStrategy);
        metricsRef.current = initialMetrics;

        applyPerformanceOptimizations(initialStrategy);

        // Notify parent components
        onMetricsUpdate?.(initialMetrics);
        onStrategyChange?.(initialStrategy);

        setIsInitialized(true);
      } catch (error) {
        console.warn('Failed to initialize performance monitoring:', error);
        setIsInitialized(true);
      }
    };

    initializeMonitoring();
  }, [collectAllMetrics, generateAdaptiveStrategy, applyPerformanceOptimizations, onMetricsUpdate, onStrategyChange]);

  // Set up periodic monitoring
  useEffect(() => {
    if (!isInitialized || (!isMobile() && getDeviceType() !== 'tablet')) {
      return;
    }

    const updateMetrics = async () => {
      try {
        const updatedMetrics = await collectAllMetrics();
        const updatedStrategy = generateAdaptiveStrategy(updatedMetrics);

        // Only update if there are significant changes
        const hasSignificantChange = 
          !metricsRef.current ||
          metricsRef.current.isLowBattery !== updatedMetrics.isLowBattery ||
          metricsRef.current.isSlowConnection !== updatedMetrics.isSlowConnection ||
          Math.abs(metricsRef.current.batteryLevel - updatedMetrics.batteryLevel) > 0.1;

        if (hasSignificantChange) {
          setMetrics(updatedMetrics);
          setStrategy(updatedStrategy);
          metricsRef.current = updatedMetrics;

          applyPerformanceOptimizations(updatedStrategy);

          onMetricsUpdate?.(updatedMetrics);
          onStrategyChange?.(updatedStrategy);
        }
      } catch (error) {
        console.warn('Failed to update performance metrics:', error);
      }
    };

    // Update every 30 seconds
    const interval = setInterval(updateMetrics, 30000);

    // Listen for network changes
    const handleConnectionChange = () => {
      setTimeout(updateMetrics, 1000); // Delay to allow connection to stabilize
    };

    const connection = (navigator as any).connection;
    if (connection) {
      connection.addEventListener('change', handleConnectionChange);
    }

    // Listen for battery changes
    if ('getBattery' in navigator) {
      (navigator as any).getBattery().then((battery: any) => {
        battery.addEventListener('levelchange', updateMetrics);
        battery.addEventListener('chargingchange', updateMetrics);
      });
    }

    return () => {
      clearInterval(interval);
      if (connection) {
        connection.removeEventListener('change', handleConnectionChange);
      }
    };
  }, [isInitialized, collectAllMetrics, generateAdaptiveStrategy, applyPerformanceOptimizations, onMetricsUpdate, onStrategyChange]);

  // Expose metrics and strategy through data attributes for debugging
  useEffect(() => {
    if (metrics && strategy) {
      document.documentElement.setAttribute('data-device-type', metrics.deviceType);
      document.documentElement.setAttribute('data-connection-type', metrics.effectiveType);
      document.documentElement.setAttribute('data-image-quality', strategy.imageQuality);
      document.documentElement.setAttribute('data-chunk-strategy', strategy.chunkLoadingStrategy);
    }
  }, [metrics, strategy]);

  return <>{children}</>;
};

export default EnhancedMobilePerformanceMonitor;